import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

export default class extends Controller {
  static targets = ["map", "list", "listButton", "mapButton", "business"]
  static values = { latitude: Number, longitude: Number }

  connect() {
    if (!this.hasMapTarget) return

    this.map = L.map(this.mapTarget, { scrollWheelZoom: false })
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    const userLocation = [this.latitudeValue, this.longitudeValue]
    L.circleMarker(userLocation, {
      radius: 8,
      color: "#ffffff",
      weight: 3,
      fillColor: "#2563eb",
      fillOpacity: 1
    }).bindTooltip("Your location").addTo(this.map)

    this.businessTargets.forEach((business) => {
      const location = [Number(business.dataset.businessLatitude), Number(business.dataset.businessLongitude)]
      const marker = L.circleMarker(location, {
        radius: 9,
        color: "#ffffff",
        weight: 3,
        fillColor: "#f97316",
        fillOpacity: 1
      }).bindPopup(this.popupContent(business)).addTo(this.map)

      marker.on("click", () => this.highlightBusiness(business))
      business.addEventListener("mouseenter", () => marker.openPopup())
      business.addEventListener("focusin", () => marker.openPopup())
    })

    this.map.setView(userLocation, 14)
  }

  disconnect() {
    this.map?.remove()
  }

  showList() {
    this.listTarget.classList.remove("hidden")
    this.mapTarget.classList.add("hidden")
    this.setActiveView("list")
  }

  showMap() {
    this.listTarget.classList.add("hidden")
    this.mapTarget.classList.remove("hidden")
    this.setActiveView("map")
    window.setTimeout(() => this.map.invalidateSize(), 0)
  }

  useLocation() {
    const status = this.element.querySelector("#location-status")

    if (!navigator.geolocation) {
      status.textContent = "Location search is not supported by this browser."
      return
    }

    status.textContent = "Finding your location..."
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.element.querySelector("#latitude").value = position.coords.latitude
        this.element.querySelector("#longitude").value = position.coords.longitude
        this.element.querySelector("#business-search-form").requestSubmit()
      },
      () => {
        status.textContent = "Location unavailable. Check your browser permission and try again."
      }
    )
  }

  popupContent(business) {
    const name = business.dataset.businessName
    const distance = business.dataset.businessDistance
    const directionsUrl = business.dataset.businessDirectionsUrl

    return `<strong>${this.escapeHtml(name)}</strong><br><span>${this.escapeHtml(distance)}</span><br><a href="${directionsUrl}" target="_blank" rel="noopener">Directions</a>`
  }

  highlightBusiness(business) {
    this.businessTargets.forEach((item) => item.classList.remove("ring-2", "ring-brand-500"))
    business.classList.add("ring-2", "ring-brand-500")
    business.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  setActiveView(view) {
    const listActive = view === "list"
    this.listButtonTarget.setAttribute("aria-pressed", listActive)
    this.mapButtonTarget.setAttribute("aria-pressed", !listActive)
    this.listButtonTarget.classList.toggle("bg-white", listActive)
    this.mapButtonTarget.classList.toggle("bg-white", !listActive)
  }

  escapeHtml(value) {
    const element = document.createElement("div")
    element.textContent = value
    return element.innerHTML
  }
}